import { useState, useEffect } from 'react';
import { Plus, Trash2, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const DEFAULT_HOLIDAYS = [
  { date: '2026-01-26', name: 'Republic Day', type: 'NATIONAL' },
  { date: '2026-03-25', name: 'Holi', type: 'NATIONAL' },
  { date: '2026-04-14', name: 'Dr. Ambedkar Jayanti', type: 'NATIONAL' },
  { date: '2026-05-01', name: 'Maharashtra Day', type: 'STATE' },
  { date: '2026-08-15', name: 'Independence Day', type: 'NATIONAL' },
  { date: '2026-10-02', name: 'Gandhi Jayanti', type: 'NATIONAL' },
  { date: '2026-10-20', name: 'Diwali', type: 'NATIONAL' },
  { date: '2026-12-25', name: 'Christmas', type: 'NATIONAL' },
];

const TYPE_COLORS = { NATIONAL: 'blue', STATE: 'purple', OPTIONAL: 'warning' };

function AddHolidayModal({ onSave, onClose }) {
  const [form, setForm] = useState({ date: '', name: '', type: 'NATIONAL' });
  const inputCls = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[18px] font-bold text-[var(--text-primary)]">Add Holiday</h2>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Date</label>
            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className={inputCls}/>
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Holiday Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Dussehra" className={inputCls}/>
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Type</label>
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className={inputCls}>
              <option value="NATIONAL">National</option>
              <option value="STATE">State</option>
              <option value="OPTIONAL">Optional</option>
            </select>
          </div>
          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={() => {
              if (!form.date || !form.name.trim()) { toast.error('Date and name are required'); return; }
              onSave(form);
              onClose();
            }}>Add Holiday</Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HolidaysPage() {
  const { data } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();
  const [holidays, setHolidays] = useState(DEFAULT_HOLIDAYS);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const saved = data?.data?.settings?.holidays;
    if (saved?.length) setHolidays(saved);
  }, [data]);

  const addHoliday = (h) => setHolidays(prev => [...prev, h].sort((a, b) => a.date.localeCompare(b.date)));
  const deleteHoliday = (date) => setHolidays(prev => prev.filter(h => h.date !== date));

  const handleSave = () => {
    mutate({ settings: { holidays } }, {
      onSuccess: () => toast.success('Holidays saved'),
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-xl">
      <PageHeader title="Holidays 2026" breadcrumb={[{label:'Settings',href:'/settings'},{label:'Holidays'}]}
        actions={<Button size="sm" icon={Plus} onClick={() => setShowModal(true)}>Add Holiday</Button>}/>
      <div className="card p-0 overflow-hidden divide-y divide-[var(--border)]">
        {holidays.length === 0 && (
          <div className="px-5 py-10 text-center text-[14px] text-[var(--text-tertiary)]">No holidays added yet</div>
        )}
        {holidays.map(h => (
          <div key={h.date} className="flex items-center justify-between px-5 py-3.5 hover:bg-[var(--bg-muted)] group">
            <div>
              <p className="text-[14px] font-semibold text-[var(--text-primary)]">{h.name}</p>
              <p className="text-[12px] text-[var(--text-tertiary)]">{new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={TYPE_COLORS[h.type] || 'blue'} size="sm">{h.type}</Badge>
              <button onClick={() => deleteHoliday(h.date)} className="p-1.5 rounded-[6px] hover:bg-[var(--danger)]/10 text-transparent group-hover:text-[var(--text-tertiary)] hover:!text-[var(--danger)] transition-all">
                <Trash2 size={13}/>
              </button>
            </div>
          </div>
        ))}
      </div>
      <Button icon={Save} onClick={handleSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save Holidays'}</Button>
      {showModal && <AddHolidayModal onSave={addHoliday} onClose={() => setShowModal(false)}/>}
    </div>
  );
}
